// MUI Color Manipulator Functions
// Based on Material-UI's internal color manipulation utilities

interface ColorObject {
  type: string;
  values: number[];
  colorSpace?: string;
}

/**
 * Returns a number whose value is limited to the given range.
 * @param {number} value The value to be clamped
 * @param {number} min The lower bound
 * @param {number} max The upper bound
 * @returns {number} A number in the range [min, max]
 */
function clamp(value: number, min: number = 0, max: number = 1): number {
  return Math.min(Math.max(min, value), max);
}

/**
 * Converts a color from CSS hex format to CSS rgb format.
 * @param {string} color - Hex color, i.e. #nnn or #nnnnnn
 * @returns {string} A CSS rgb color string
 */
function hexToRgb(color: string): string {
  color = color.slice(1);

  const re = new RegExp(`.{1,${color.length >= 6 ? 2 : 1}}`, "g");
  let colors = color.match(re) as RegExpMatchArray | null;

  if (colors && colors[0].length === 1) {
    colors = colors.map((n: string) => n + n) as RegExpMatchArray;
  }

  return colors
    ? `rgb${colors.length === 4 ? "a" : ""}(${colors
        .map((n: string, index: number) => {
          return index < 3
            ? parseInt(n, 16)
            : Math.round((parseInt(n, 16) / 255) * 1000) / 1000;
        })
        .join(", ")})`
    : "";
}

/**
 * Returns an object with the type and values of a color.
 * Note: Does not support rgb % values.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @returns {object} - A MUI color object: {type: string, values: number[]}
 */
function decomposeColor(color: string | ColorObject): ColorObject {
  // Idempotent
  if (typeof color === "object" && color.type) {
    return color;
  }

  if (typeof color !== "string") {
    throw new Error("Color must be a string or ColorObject");
  }

  if (color.charAt(0) === "#") {
    return decomposeColor(hexToRgb(color));
  }

  const marker = color.indexOf("(");
  const type = color.substring(0, marker);

  if (["rgb", "rgba", "hsl", "hsla", "color"].indexOf(type) === -1) {
    throw new Error(
      `MUI: Unsupported \`${color}\` color.\n` +
        "The following formats are supported: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color().",
    );
  }

  let values = color.substring(marker + 1, color.length - 1);
  let colorSpace: string | undefined;

  if (type === "color") {
    const valuesArray = values.split(" ");
    colorSpace = valuesArray.shift();
    if (valuesArray.length === 4 && valuesArray[3].charAt(0) === "/") {
      valuesArray[3] = valuesArray[3].slice(1);
    }
    values = valuesArray.join(",");
    if (
      ["srgb", "display-p3", "a98-rgb", "prophoto-rgb", "rec-2020"].indexOf(
        colorSpace!,
      ) === -1
    ) {
      throw new Error(
        `MUI: unsupported \`${colorSpace}\` color space.\n` +
          "The following color spaces are supported: srgb, display-p3, a98-rgb, prophoto-rgb, rec-2020.",
      );
    }
  }
  const parsedValues = values
    .split(",")
    .map((value: string) => parseFloat(value));

  return { type, values: parsedValues, colorSpace };
}

/**
 * Converts a color object with type and values to a string.
 * @param {object} color - Decomposed color
 * @returns {string} A CSS color string
 */
function recomposeColor(color: ColorObject): string {
  const { type, colorSpace } = color;
  let values: (number | string)[] | string = [...color.values];

  if (type.indexOf("rgb") !== -1) {
    // Only convert the first 3 values to int (i.e. not alpha)
    values = values.map((n: number | string, i: number) =>
      i < 3 ? parseInt(String(n), 10) : n,
    );
  } else if (type.indexOf("hsl") !== -1) {
    values = values.map((n: number | string, i: number) => {
      if (i === 1 || i === 2) return `${n}%`;
      return n;
    });
  }

  if (type.indexOf("color") !== -1) {
    values = `${colorSpace} ${values.join(" ")}`;
  } else {
    values = `${values.join(", ")}`;
  }

  return `${type}(${values})`;
}

/**
 * Darkens a color.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @param {number} coefficient - multiplier in the range 0 - 1
 * @returns {string} A CSS color string. Hex input values are returned as rgb
 */
function darken(color: string, coefficient: number): string {
  const decomposed = decomposeColor(color);
  coefficient = clamp(coefficient);

  if (decomposed.type.indexOf("hsl") !== -1) {
    decomposed.values[2] *= 1 - coefficient;
  } else if (
    decomposed.type.indexOf("rgb") !== -1 ||
    decomposed.type.indexOf("color") !== -1
  ) {
    for (let i = 0; i < 3; i += 1) {
      decomposed.values[i] *= 1 - coefficient;
    }
  }
  return recomposeColor(decomposed);
}

/**
 * Lightens a color.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @param {number} coefficient - multiplier in the range 0 - 1
 * @returns {string} A CSS color string. Hex input values are returned as rgb
 */
function lighten(color: string, coefficient: number): string {
  const decomposed = decomposeColor(color);
  coefficient = clamp(coefficient);

  if (decomposed.type.indexOf("hsl") !== -1) {
    decomposed.values[2] += (100 - decomposed.values[2]) * coefficient;
  } else if (decomposed.type.indexOf("rgb") !== -1) {
    for (let i = 0; i < 3; i += 1) {
      decomposed.values[i] += (255 - decomposed.values[i]) * coefficient;
    }
  } else if (decomposed.type.indexOf("color") !== -1) {
    for (let i = 0; i < 3; i += 1) {
      decomposed.values[i] += (1 - decomposed.values[i]) * coefficient;
    }
  }
  return recomposeColor(decomposed);
}

/**
 * Set the absolute transparency of a color.
 * Any existing alpha values are overwritten.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @param {number} value - value to set the alpha channel to in the range 0-1
 * @returns {string} A CSS color string. Hex input values are returned as rgb
 */
function alpha(color: string, value: number): string {
  const decomposed = decomposeColor(color);
  value = clamp(value);

  if (decomposed.type === "rgb" || decomposed.type === "hsl") {
    decomposed.type += "a";
  }
  if (decomposed.type === "color") {
    decomposed.values[3] = Number(`${value}`);
  } else {
    decomposed.values[3] = value;
  }

  return recomposeColor(decomposed);
}

/**
 * Converts a color from CSS hsl format to CSS rgb format.
 * @param {string} color - HSL color values
 * @returns {string} rgb color values
 */
function hslToRgb(color: string): string {
  const decomposed = decomposeColor(color);
  const { values } = decomposed;
  const h = values[0];
  const s = values[1] / 100;
  const l = values[2] / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number, k: number = (n + h / 30) % 12) =>
    l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

  let type = "rgb";
  const rgb = [
    Math.round(f(0) * 255),
    Math.round(f(8) * 255),
    Math.round(f(4) * 255),
  ];

  if (decomposed.type === "hsla") {
    type += "a";
    rgb.push(values[3]);
  }

  return recomposeColor({ type, values: rgb });
}

/**
 * The relative brightness of any point in a color space,
 * normalized to 0 for darkest black and 1 for lightest white.
 *
 * Formula: https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @returns {number} The relative brightness of the color in the range 0 - 1
 */
function getLuminance(color: string): number {
  const decomposed = decomposeColor(color);
  let rgb =
    decomposed.type === "hsl"
      ? decomposeColor(hslToRgb(color)).values
      : decomposed.values;

  rgb = rgb.map((val: number) => {
    if (decomposed.type !== "color") {
      val /= 255; // normalized
    }
    return val <= 0.03928 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4;
  });

  // Truncate at 3 digits
  return Number(
    (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]).toFixed(3),
  );
}

/**
 * Calculates the contrast ratio between two colors.
 *
 * Formula: https://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef
 * @param {string} foreground - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param {string} background - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @returns {number} A contrast ratio value in the range 0 - 21
 */
function getContrastRatio(foreground: string, background: string): number {
  const lumA = getLuminance(foreground);
  const lumB = getLuminance(background);
  return (Math.max(lumA, lumB) + 0.05) / (Math.min(lumA, lumB) + 0.05);
}

/**
 * Returns black or white for best contrast depending on the given background color.
 * @param {string} background - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param {number} threshold - Contrast threshold (default: 3)
 * @returns {string} Either '#fff' for white text or 'rgba(0, 0, 0, 0.87)' for dark text
 */
function getContrastText(background: string, threshold: number = 3): string {
  return getContrastRatio(background, "#fff") >= threshold
    ? "#fff"
    : "rgba(0, 0, 0, 0.87)";
}

/**
 * Darken or lighten a color, depending on its luminance.
 * Light colors are darkened, dark colors are lightened.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param {number} coefficient - multiplier in the range 0 - 1 (default: 0.15)
 * @returns {string} A CSS color string. Hex input values are returned as rgb
 */
function emphasize(color: string, coefficient: number = 0.15): string {
  return getLuminance(color) > 0.1
    ? darken(color, coefficient)
    : lighten(color, coefficient);
}

/**
 * Checks if a color is considered a default/system color (black or white variants)
 * @param {string} color - CSS color to check
 * @returns {boolean} True if the color is a default color that should be auto-updated
 */
function isDefaultColor(color: string | undefined | null): boolean {
  if (!color) return true;

  // Normalize color to check against common black/white values
  const normalizedColor = color.toLowerCase().trim();

  // Check for common black values
  return (
    normalizedColor === "#000000" ||
    normalizedColor === "#000" ||
    normalizedColor === "rgb(0, 0, 0)" ||
    normalizedColor === "rgba(0, 0, 0, 1)" ||
    normalizedColor === "rgba(0, 0, 0, 0.87)" ||
    normalizedColor === "black" ||
    // Check for common white values
    normalizedColor === "#ffffff" ||
    normalizedColor === "#fff" ||
    normalizedColor === "rgb(255, 255, 255)" ||
    normalizedColor === "rgba(255, 255, 255, 1)" ||
    normalizedColor === "white"
  );
}

/**
 * Calculates an appropriate contrast color based on a background color
 * For light backgrounds, returns a darkened version
 * For dark backgrounds, returns a lightened version
 * @param {string} backgroundColor - The background color to base the contrast on
 * @param {number} coefficient - The amount to lighten/darken (default: 0.1)
 * @returns {string} The calculated contrast color
 */
function calculateContrastColor(
  backgroundColor: string,
  coefficient: number = 0.1,
): string {
  return emphasize(backgroundColor, coefficient);
}

/**
 * @deprecated Use calculateContrastColor instead. This function will be removed in a future version.
 * Calculates an appropriate stroke color based on a background color
 * For light backgrounds, returns a darkened version
 * For dark backgrounds, returns a lightened version
 * @param {string} backgroundColor - The background color to base the stroke on
 * @param {number} coefficient - The amount to lighten/darken (default: 0.1)
 * @returns {string} The calculated stroke color
 */
function calculateStrokeColor(
  backgroundColor: string,
  coefficient: number = 0.1,
): string {
  return calculateContrastColor(backgroundColor, coefficient);
}

// Export functions for use
export {
  lighten,
  darken,
  alpha,
  emphasize,
  getContrastText,
  getContrastRatio,
  getLuminance,
  clamp,
  hexToRgb,
  hslToRgb,
  decomposeColor,
  recomposeColor,
  isDefaultColor,
  calculateContrastColor,
  calculateStrokeColor,
};

// Example usage:
// const lightBlue = lighten('#1976d2', 0.3);           // Lighten by 30%
// const darkBlue = darken('#1976d2', 0.2);             // Darken by 20%
// const transparent = alpha('#1976d2', 0.5);           // 50% transparency
// const contrastText = getContrastText('#1976d2');     // Returns '#fff' or dark text
// const ratio = getContrastRatio('#fff', '#1976d2');   // Returns contrast ratio
// const luminance = getLuminance('#1976d2');           // Returns luminance value
// const emphasized = emphasize('#1976d2');             // Auto darken/lighten
